/**
 * App providers wrapper component
 * Centralizes all context providers and sets up global error handling
 */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { ToastProvider, useToast } from '../contexts/ToastContext';
import errorHandler from '../services/errorHandler';
import logger from '../services/logger';
import apiService from '../services/api';

// Component to set up error handling integration
const ErrorHandlerSetup = ({ children }) => {
  const {
    error: showErrorToast,
    success: showSuccessToast,
    warning: showWarningToast,
  } = useToast();

  useEffect(() => {
    // Set up global toast function for error handler
    window.showToast = (message, options = {}) => {
      const { type = 'info', ...toastOptions } = options;

      switch (type) {
        case 'error':
          showErrorToast(message, toastOptions);
          break;
        case 'success':
          showSuccessToast(message, toastOptions);
          break;
        case 'warning':
          showWarningToast(message, toastOptions);
          break;
        default:
          showErrorToast(message, toastOptions);
      }
    };

    // Set up global error handler
    const handleGlobalError = (error, _context) => {
      // Log error for analytics
      logger.error('Global error occurred', error);

      // Show user-friendly message
      errorHandler.showUserFriendlyMessage(error);
    };

    // Add error listener
    errorHandler.addErrorListener(handleGlobalError);

    // Set up API service authentication
    const token = localStorage.getItem('token');
    if (token) {
      apiService.setAuthToken(token);
    }

    // Clean up
    return () => {
      errorHandler.removeErrorListener(handleGlobalError);
      delete window.showToast;
    };
  }, [showErrorToast, showSuccessToast, showWarningToast]);

  return children;
};

ErrorHandlerSetup.propTypes = {
  children: PropTypes.node.isRequired,
};

// Main providers wrapper
const AppProviders = ({ children }) => {
  return (
    <ToastProvider>
      <ErrorHandlerSetup>{children}</ErrorHandlerSetup>
    </ToastProvider>
  );
};

AppProviders.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppProviders;
